import { useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';

// Créer un contexte pour partager l'état des modifications non sauvegardées
const UnsavedChangesContext = createContext(null);

/**
 * Provider pour partager l'état des modifications non sauvegardées
 */
export function UnsavedChangesProvider({ children }) {
  const hasUnsavedChangesRef = useRef(false);
  const currentMessageRef = useRef('');
  const allowNextNavigationRef = useRef(false);

  const contextValue = {
    hasUnsavedChangesRef,
    currentMessageRef,
    allowNextNavigationRef,
    setHasUnsavedChanges: (value, message) => {
      hasUnsavedChangesRef.current = value;
      if (message) {
        currentMessageRef.current = message;
      }
    },
    allowNextNavigation: () => {
      allowNextNavigationRef.current = true;
    }
  };

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

/**
 * Hook pour utiliser le contexte des modifications non sauvegardées
 */
export function useUnsavedChangesContext() {
  const context = useContext(UnsavedChangesContext);
  if (!context) {
    throw new Error('useUnsavedChangesContext must be used within UnsavedChangesProvider');
  }
  return context;
}

/**
 * Hook pour avertir l'utilisateur avant de quitter la page avec des modifications non sauvegardées
 * @param {boolean} hasUnsavedChanges - Indique si des modifications non sauvegardées existent
 * @param {string} message - Message d'avertissement
 * @returns {function} allowNavigation - Fonction pour permettre la prochaine navigation
 * @returns {function} setHasUnsavedChanges - Fonction pour mettre à jour l'état
 */
export function useUnsavedChangesWarning(
  hasUnsavedChanges = false,
  message = 'Vous avez des modifications non enregistrées. Voulez-vous vraiment quitter cette page ?'
) {
  const context = useUnsavedChangesContext();
  const { hasUnsavedChangesRef, currentMessageRef, allowNextNavigationRef, setHasUnsavedChanges: setContextUnsavedChanges } = context;

  // Mettre à jour le contexte quand les props changent
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
    if (message) {
      currentMessageRef.current = message;
    }
  }, [hasUnsavedChanges, message, hasUnsavedChangesRef, currentMessageRef]);

  // Fonction pour autoriser la prochaine navigation (à appeler avant de naviguer après sauvegarde)
  const allowNavigation = useCallback(() => {
    allowNextNavigationRef.current = true;
  }, [allowNextNavigationRef]);

  // Bloquer le refresh et la fermeture du navigateur
  useBeforeUnload(
    useCallback((event) => {
      if (hasUnsavedChanges && !allowNextNavigationRef.current) {
        event.preventDefault();
        return (event.returnValue = message);
      }
    }, [hasUnsavedChanges, message, allowNextNavigationRef])
  );

  // Fonction pour mettre à jour l'état (compatibilité)
  const setHasUnsavedChanges = useCallback((value, msg) => {
    setContextUnsavedChanges(value, msg);
  }, [setContextUnsavedChanges]);

  return { allowNavigation, setHasUnsavedChanges };
}

/**
 * Hook pour créer une fonction de navigation sécurisée
 * Cette fonction doit être appelée au lieu de navigate() direct
 */
export function useSafeNavigate() {
  const navigate = useNavigate();
  const context = useUnsavedChangesContext();
  const { hasUnsavedChangesRef, currentMessageRef, allowNextNavigationRef } = context;

  return useCallback((to, options) => {
    console.log('🔍 useSafeNavigate appelé:', { to, currentPath: window.location.pathname });
    console.log('🔍 hasUnsavedChangesRef.current:', hasUnsavedChangesRef.current);
    console.log('🔍 allowNextNavigationRef.current:', allowNextNavigationRef.current);
    
    // Si on navigue vers la même page, ne rien faire
    if (window.location.pathname === to) {
      console.log('⚠️ Même page, navigation ignorée');
      return;
    }
    
    // Si on a des modifications non sauvegardées et qu'on n'a pas autorisé la navigation
    if (hasUnsavedChangesRef.current && !allowNextNavigationRef.current) {
      console.log('⚠️ Modifications non sauvegardées détectées, demande de confirmation...');
      // Demander confirmation
      const shouldNavigate = window.confirm(currentMessageRef.current);
      console.log('✅ Confirmation:', shouldNavigate);
      if (!shouldNavigate) {
        console.log('❌ Navigation annulée par l\'utilisateur');
        return;
      }
    }
    // Réinitialiser les flags après navigation (même si l'utilisateur a annulé les modifications)
    hasUnsavedChangesRef.current = false;
    allowNextNavigationRef.current = false;
    console.log('✅ Navigation autorisée vers:', to);
    navigate(to, options);
  }, [navigate, hasUnsavedChangesRef, currentMessageRef, allowNextNavigationRef]);
}

